/**
 * Register Page
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { EmailPhoneToggle, InputWithIcon } from '@shared/components/form'
import { PhoneInput } from '@shared/components/forms'
import { Button } from '@shared/components/ui/button'
import { Card } from '@shared/components/ui/card'
import { Checkbox } from '@shared/components/ui/checkbox'
import { TermsOfServiceModal } from '@shared/components/TermsOfServiceModal'
import { useAuth } from '@shared/contexts'
import { registerSchema, type RegisterFormData } from '@shared/lib/validators'
import { Info, Mail, UserPlus } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

export function RegisterPage() {
  const { register: registerUser } = useAuth()
  const navigate = useNavigate()
  const [loginWithPhone, setLoginWithPhone] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      loginWithPhone: true,
      toc: false,
      email: '',
      phoneNumber: '',
      password: '',
      repeatPassword: '',
    },
  })

  const tocValue = watch('toc')

  const toggleLoginMethod = useCallback(() => {
    setLoginWithPhone((prev) => !prev)
    setValue('loginWithPhone', !loginWithPhone)
    // Clear both fields when toggling
    setValue('email', '')
    setValue('phoneNumber', '')
  }, [loginWithPhone, setValue])

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true)

    try {
      // Prepare phone number with Argentina prefix if needed
      let identifier = data.email || data.phoneNumber || ''

      if (loginWithPhone && identifier) {
        // Clean and add prefix
        const cleaned = identifier.replace(/\D/g, '')
        if (cleaned.length === 10) {
          identifier = '549' + cleaned
        }
      }

      await registerUser({
        email: identifier,
        phone: identifier,
        password: data.password,
        confirmPassword: data.repeatPassword,
        firstName: '', // Will be filled in profile later
        lastName: '',
      })

      toast.success('Cuenta creada exitosamente!')

      // Google Ads conversion tracking — fire "Creación de cuenta" event
      // TODO: Replace REPLACE_WITH_CONVERSION_LABEL with the actual label from Google Ads
      if (typeof gtag === 'function') {
        gtag('event', 'conversion', {
          send_to: 'AW-17559912774/REPLACE_WITH_CONVERSION_LABEL',
        })
      }

      void navigate('/')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al crear la cuenta')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-auth bg-cover bg-center p-4">
      <Card className="w-full max-w-md p-6 shadow-sm">
        <h1 className="text-2xl font-bold text-center mb-6">Crear Cuenta</h1>

        {/* Email/Phone Toggle */}
        <EmailPhoneToggle
          isPhone={loginWithPhone}
          onToggle={toggleLoginMethod}
          disabled={true}
        />

        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4 mt-6">
          {/* Conditional Email or Phone Input */}
          {loginWithPhone ? (
            <div>
              <PhoneInput
                id="phoneNumber"
                {...register('phoneNumber')}
                value={watch('phoneNumber') || ''}
                onChange={(value) => setValue('phoneNumber', value)}
                placeholder="Número de teléfono"
              />
              {errors.phoneNumber && (
                <p className="text-sm text-destructive mt-1">{errors.phoneNumber.message}</p>
              )}
            </div>
          ) : (
            <div>
              <InputWithIcon
                icon={Mail}
                id="email"
                type="email"
                {...register('email')}
                placeholder="Correo electrónico"
              />
              {errors.email && (
                <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
              )}
            </div>
          )}

          {/* Password */}
          <div>
            <InputWithIcon
              isPasswordToggle
              id="password"
              type="password"
              placeholder="Contraseña"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-sm text-destructive mt-1">{errors.password.message}</p>
            )}
          </div>

          {/* Repeat Password */}
          <div>
            <InputWithIcon
              isPasswordToggle
              id="repeatPassword"
              type="password"
              placeholder="Confirmar contraseña"
              {...register('repeatPassword')}
            />
            {errors.repeatPassword && (
              <p className="text-sm text-destructive mt-1">{errors.repeatPassword.message}</p>
            )}
          </div>

          {/* Password Requirements */}
          <div className="bg-muted p-3 rounded-md flex gap-2">
            <Info className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">Importante</p>
              <p>• Contraseña de más de 8 caracteres</p>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="flex items-start space-x-2">
            <Checkbox id="toc" checked={tocValue} onCheckedChange={(checked) => setValue('toc', checked as boolean)} />
            <label htmlFor="toc" className="text-sm leading-none cursor-pointer">
              Acepto los{' '}
              <button
                type="button"
                onClick={() => setShowTermsModal(true)}
                className="underline hover:text-primary bg-transparent border-none p-0 font-inherit cursor-pointer"
              >
                términos y condiciones
              </button>{' '}
              de uso
            </label>
          </div>
          {errors.toc && <p className="text-sm text-destructive">{errors.toc.message}</p>}

          {/* Submit Button */}
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <span>Creando cuenta...</span>
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Registrarme
              </>
            )}
          </Button>

          {/* Login Link */}
          <div className="text-center text-sm text-muted-foreground">
            ¿Ya tienes una cuenta?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Iniciar sesión
            </Link>
          </div>
        </form>
      </Card>

      {/* Terms of Service Modal */}
      <TermsOfServiceModal open={showTermsModal} onOpenChange={setShowTermsModal} />
    </div>
  )
}
